/**
 * El cliente usa camelCase y Postgres usa snake_case. La conversion vive aqui,
 * en el limite del sistema, y en ningun otro sitio.
 */
export function camelToSnake(key: string): string {
  return key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
}

export function snakeToCamel(key: string): string {
  return key.replace(/_([a-z0-9])/g, (_match, letter: string) => letter.toUpperCase());
}

type Plain = Record<string, unknown>;

function isPlainObject(value: unknown): value is Plain {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Campos cuyas CLAVES son datos, no nombres de campo.
 *
 * La conversion es recursiva porque dentro de un `jsonb` tambien hay nombres
 * de campo, y esos viajan en camelCase en el cliente y en snake_case en el
 * servidor. Pero en `volumeTargets` y `musclePriorities` la clave es un
 * MUSCULO, y los musculos se escriben en snake_case en los dos lados:
 * `deltoide_posterior` bajaba como `deltoidePosterior`, que no es ningun
 * musculo, y el bloque entero dejaba de encajar con su contrato. Solo en un
 * dispositivo que lo bajara de cero —el que lo crea no lo rebaja—, y solo con
 * los tres deltoides, que son los unicos musculos con guion bajo (22/09/2026).
 *
 * El nombre del campo se convierte; sus claves, no. Van las dos formas porque
 * la subida ve el nombre en camelCase y la bajada en snake_case.
 *
 * `payload` (de `ai_decisions`) va por otro motivo: es la respuesta de la IA
 * tal como la escribio la Edge Function, en camelCase, y la Edge Function la
 * VUELVE A SERVIR en cada acierto de cache. Al subir el veredicto, el cliente
 * la reescribia en snake_case (`volume_targets`, `covers_from`) y el siguiente
 * acierto de cache devolvia algo que el esquema del cliente rechaza: camino
 * determinista, sin coste y sin aviso. Se ve en produccion desde el 23/09/2026
 * en la fila de la revision de literatura. Aqui el contenido entero es opaco.
 */
const CLAVES_QUE_SON_DATOS = new Set([
  'volumeTargets',
  'volume_targets',
  'musclePriorities',
  'muscle_priorities',
  'payload',
]);

function convertir(value: unknown, clave: (key: string) => string): unknown {
  if (Array.isArray(value)) return value.map((item) => convertir(item, clave));
  if (!isPlainObject(value)) return value;
  const out: Plain = {};
  for (const [key, item] of Object.entries(value)) {
    // Copia superficial a proposito: lo de dentro no se toca a ninguna
    // profundidad, que es lo que hace falta en `payload`.
    out[clave(key)] =
      CLAVES_QUE_SON_DATOS.has(key) && isPlainObject(item) ? { ...item } : convertir(item, clave);
  }
  return out;
}

/**
 * Devuelve a su forma las claves que la bajada antigua estropeo en una fila
 * LOCAL: `deltoidePosterior` → `deltoide_posterior`.
 *
 * Hace falta ademas de arreglar la conversion porque una fila ya guardada no se
 * vuelve a escribir al bajar mientras el servidor no la cambie: con la misma
 * version, `sync_pull` solo anota el `syncSeq`. Lo que se deja es exactamente
 * lo que tiene el servidor, asi que no se encola nada.
 *
 * Devuelve `null` si no habia nada que tocar, para no reescribir filas sanas.
 */
export function restaurarClavesQueSonDatos(
  row: Record<string, unknown>,
): Record<string, unknown> | null {
  let cambiada = false;
  const out: Plain = { ...row };
  for (const campo of ['volumeTargets', 'musclePriorities']) {
    const valor = row[campo];
    if (!isPlainObject(valor)) continue;
    const reparado: Plain = {};
    for (const [key, item] of Object.entries(valor)) {
      const buena = camelToSnake(key);
      if (buena !== key) cambiada = true;
      reparado[buena] = item;
    }
    out[campo] = reparado;
  }
  return cambiada ? out : null;
}

export function keysToSnake(value: unknown): unknown {
  return convertir(value, camelToSnake);
}

export function keysToCamel(value: unknown): unknown {
  return convertir(value, snakeToCamel);
}

/** Sellos de tiempo comunes a todas las entidades sincronizables. */
const SELLOS = ['createdAt', 'updatedAt', 'deletedAt'] as const;

/**
 * Deja los sellos de tiempo en el formato canonico del cliente.
 *
 * Por que hace falta: el cliente escribe `Date.toISOString()`, que termina en
 * `Z`, y Postgres devuelve el mismo instante como `+00:00`. Son cadenas
 * distintas para el mismo momento, asi que comparar por igualdad de texto dice
 * "han cambiado" en filas que no han cambiado nada.
 *
 * Se normaliza al entrar, no al comparar, para que lo guardado en local tenga
 * un unico formato y ninguna comparacion futura pueda caer en la misma trampa.
 */
export function normalizarSellos(row: Record<string, unknown>): Record<string, unknown> {
  const out: Plain = { ...row };
  for (const sello of SELLOS) {
    const valor = out[sello];
    if (typeof valor !== 'string') continue;
    const instante = Date.parse(valor);
    // Una fecha ilegible se deja intacta: no es tarea de esta funcion decidir
    // que hacer con ella, y silenciarla la escondería.
    if (!Number.isFinite(instante)) continue;
    out[sello] = new Date(instante).toISOString();
  }
  return out;
}
